/**
 * Core Notifications service
 */

import { EventEmitter } from 'eventemitter3';
import { 
  NotificationService,
  NotificationPayload,
  CreateNotificationInput,
  NotificationFilters,
  NotificationCount,
  NotificationConfig,
  NotificationHooks,
  NotificationEvents,
  NotificationType,
  NotificationTypeConfig
} from './types';
import { StorageAdapter, WebSocketAdapter } from './types';

export class Notifications extends EventEmitter implements NotificationService {
  private storageAdapter: StorageAdapter;
  private websocketAdapter?: WebSocketAdapter;
  private hooks?: NotificationHooks;
  private defaultType: string;
  private maxRetries: number;
  private retryDelay: number;
  private notificationTypes: Map<string, NotificationTypeConfig> = new Map();

  constructor(config: NotificationConfig) {
    super();
    this.storageAdapter = config.storageAdapter;
    this.websocketAdapter = config.websocketAdapter;
    this.hooks = config.hooks;
    this.defaultType = config.defaultType || 'info';
    this.maxRetries = config.maxRetries || 3;
    this.retryDelay = config.retryDelay || 1000;

    // Register default notification types
    this.registerDefaultTypes();
  }

  /**
   * Register a notification type with its configuration
   */
  registerType(type: string, config: NotificationTypeConfig): void {
    this.notificationTypes.set(type, config);
  }

  /**
   * Get notification type configuration
   */
  getTypeConfig(type: string): NotificationTypeConfig | undefined {
    return this.notificationTypes.get(type);
  }

  /**
   * Create a new notification
   */
  async create(input: CreateNotificationInput): Promise<NotificationPayload> {
    try {
      // Apply before create hook
      let processedInput = input;
      if (this.hooks?.onBeforeCreate) {
        processedInput = await this.hooks.onBeforeCreate(input);
      }

      // Generate ID
      const id = this.generateId();
      const now = new Date();

      // Create notification payload
      const notification: NotificationPayload = {
        id,
        ...processedInput,
        type: processedInput.type || this.defaultType,
        read: false,
        createdAt: now,
        updatedAt: now,
      };

      // Save to storage
      const savedNotification = await this.storageAdapter.save(notification);

      // Apply after create hook
      if (this.hooks?.onAfterCreate) {
        await this.hooks.onAfterCreate(savedNotification);
      }

      // Emit events
      this.emit('notification:created', savedNotification);

      // Broadcast via WebSocket if available
      if (this.websocketAdapter) {
        try {
          await this.websocketAdapter.broadcastToRecipient(
            savedNotification.recipientId,
            savedNotification
          );
        } catch (error) {
          console.warn('Failed to broadcast notification via WebSocket:', error);
        }
      }

      return savedNotification;
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Get notifications with optional filters
   */
  async get(filters?: NotificationFilters): Promise<NotificationPayload[]> {
    try {
      return await this.storageAdapter.find(filters || {});
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Get a single notification by ID
   */
  async getOne(id: string): Promise<NotificationPayload | null> {
    try {
      return await this.storageAdapter.findOne(id);
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<NotificationPayload | null> {
    try {
      const notification = await this.storageAdapter.findOne(id);
      if (!notification) {
        return null;
      }

      const updatedNotification = await this.storageAdapter.update(id, { read: true });
      if (updatedNotification) {
        this.emit('notification:markedAsRead', { 
          id, 
          recipientId: updatedNotification.recipientId 
        });
        this.emit('notification:updated', updatedNotification);

        // Broadcast via WebSocket if available
        if (this.websocketAdapter) {
          try {
            await this.websocketAdapter.broadcastToRecipient(
              updatedNotification.recipientId,
              updatedNotification
            );
          } catch (error) {
            console.warn('Failed to broadcast mark as read via WebSocket:', error);
          }
        }
      }

      return updatedNotification;
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as unread
   */
  async markAsUnread(id: string): Promise<NotificationPayload | null> {
    try {
      const notification = await this.storageAdapter.findOne(id);
      if (!notification) {
        return null;
      }

      const updatedNotification = await this.storageAdapter.update(id, { read: false });
      if (updatedNotification) {
        this.emit('notification:markedAsUnread', { 
          id, 
          recipientId: updatedNotification.recipientId 
        });
        this.emit('notification:updated', updatedNotification);

        // Broadcast via WebSocket if available
        if (this.websocketAdapter) {
          try {
            await this.websocketAdapter.broadcastToRecipient(
              updatedNotification.recipientId,
              updatedNotification
            );
          } catch (error) {
            console.warn('Failed to broadcast mark as unread via WebSocket:', error);
          }
        }
      }

      return updatedNotification;
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read/unread for a recipient
   */
  async markAll(recipientId: string, read: boolean): Promise<number> {
    try {
      // Get all notifications for the recipient
      const notifications = await this.storageAdapter.find({ recipientId });
      
      // Update each notification
      let count = 0;
      for (const notification of notifications) {
        if (notification.read !== read) {
          await this.storageAdapter.update(notification.id, { read });
          count++;
        }
      }
      
      this.emit('notification:markedAll', { 
        recipientId, 
        read, 
        count 
      });

      // Broadcast via WebSocket if available
      if (this.websocketAdapter) {
        try {
          await this.websocketAdapter.broadcastToRecipient(
            recipientId,
            { recipientId, read, count } as any
          );
        } catch (error) {
          console.warn('Failed to broadcast mark all via WebSocket:', error);
        }
      }

      return count;
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for a recipient
   */
  async markAllAsRead(recipientId: string | number, category?: string): Promise<{ success: boolean; updatedCount?: number; error?: string }> {
    try {
      const count = await this.markAll(recipientId.toString(), true);
      return { success: true, updatedCount: count };
    } catch (error) {
      console.error('Error in notifications service:', error);
      return { success: false, error: 'Failed to mark all notifications as read' };
    }
  }

  /**
   * Delete a notification
   */
  async delete(id: string): Promise<boolean> {
    try {
      // Apply before delete hook
      if (this.hooks?.onBeforeDelete) {
        const shouldDelete = await this.hooks.onBeforeDelete(id);
        if (!shouldDelete) {
          return false;
        }
      }

      const success = await this.storageAdapter.delete(id);
      
      if (success) {
        this.emit('notification:deleted', { id });

        // Apply after delete hook
        if (this.hooks?.onAfterDelete) {
          await this.hooks.onAfterDelete(id);
        }
      }

      return success;
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Get notification count with optional filters
   */
  async count(filters?: Omit<NotificationFilters, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>): Promise<NotificationCount> {
    try {
      const total = await this.storageAdapter.count(filters || {});
      const unread = await this.storageAdapter.count({ ...filters, read: false });
      const read = total - unread;
      
      // Get counts by type
      const notifications = await this.storageAdapter.find(filters || {});
      const byType: Record<string, number> = {};
      notifications.forEach(notification => {
        byType[notification.type] = (byType[notification.type] || 0) + 1;
      });

      return {
        total,
        unread,
        read,
        byType
      };
    } catch (error) {
      console.error('Error in notifications service:', error);
      throw error;
    }
  }

  /**
   * Emit a custom event
   */
  emitEvent(event: keyof NotificationEvents, data: any): boolean {
    return super.emit(event, data);
  }

  /**
   * Register event listener
   */
  onEvent(event: keyof NotificationEvents, callback: (data: any) => void): this {
    return super.on(event, callback);
  }

  /**
   * Remove event listener
   */
  offEvent(event: keyof NotificationEvents, callback: (data: any) => void): this {
    return super.off(event, callback);
  }

  /**
   * Get WebSocket adapter
   */
  getWebSocketAdapter(): WebSocketAdapter | undefined {
    return this.websocketAdapter;
  }

  /**
   * Get storage adapter
   */
  getStorageAdapter(): StorageAdapter {
    return this.storageAdapter;
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Register default notification types
   */
  private registerDefaultTypes(): void {
    const defaultTypes: Record<string, NotificationTypeConfig> = {
      info: {
        defaultPriority: 'normal',
        defaultCategory: 'general',
      },
      success: {
        defaultPriority: 'normal',
        defaultCategory: 'general',
      },
      warning: {
        defaultPriority: 'high',
        defaultCategory: 'general',
      },
      error: {
        defaultPriority: 'urgent',
        defaultCategory: 'general',
      },
      message: {
        defaultPriority: 'normal',
        defaultCategory: 'general',
      },
      system: {
        defaultPriority: 'low',
        defaultCategory: 'system',
      },
      order: {
        defaultPriority: 'normal',
        defaultCategory: 'order',
      },
      payment: {
        defaultPriority: 'high',
        defaultCategory: 'payment',
      },
      shipping: {
        defaultPriority: 'normal',
        defaultCategory: 'shipping',
      },
      custom: {
        defaultPriority: 'normal',
        defaultCategory: 'custom',
      },
    };

    Object.entries(defaultTypes).forEach(([type, config]) => {
      this.registerType(type, config);
    });
  }
}
